import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountStatusService,
  AccountStatus,
  StatusBanner,
} from '../core/services/account-status.service';

interface BannerCTA {
  label: string;
  route: string;
  /** Always an object (never undefined) so the `[queryParams]` binding stays referentially stable. */
  queryParams: Record<string, string>;
}

/**
 * One banner, with its message and CTAs already resolved.
 *
 * These used to be methods called straight from the template. Both returned a
 * freshly built array/string on every change-detection pass, so the `*ngFor`
 * over the CTAs saw a completely new set of items each time and rebuilt the
 * `<a>` elements. A `mousedown` runs change detection, which destroyed the very
 * anchor being pressed, so the following `mouseup` landed on a new element and
 * the browser never fired a `click` — the Upgrade button looked dead. Resolving
 * everything once, when the status arrives, keeps the DOM nodes stable.
 */
interface BannerView {
  banner: StatusBanner;
  message: string;
  ctas: BannerCTA[];
}

@Component({
  selector: 'app-account-status-banner',
  templateUrl: './account-status-banner.component.html',
  styleUrls: ['./account-status-banner.component.css'],
})
export class AccountStatusBannerComponent implements OnInit {
  /** Set to false on pages where the user is already taking action (e.g. subscription page). */
  @Input() allowOverlay = true;

  status: AccountStatus | null = null;
  isStorageFullOverlay = false;
  visibleBanners: BannerView[] = [];

  /** In-memory only — cleared on every page reload. */
  private dismissedThisSession = new Set<string>();

  constructor(
    private accountStatusService: AccountStatusService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    await this.accountStatusService.fetchStatus();
    this.accountStatusService.status$.subscribe((status) => {
      this.status = status;
      if (status) {
        this.recalcOverlay();
        this.visibleBanners = this.buildViews(status.banners || []);
      }
    });
  }

  private recalcOverlay(): void {
    if (!this.status || !this.allowOverlay) {
      this.isStorageFullOverlay = false;
      return;
    }
    this.isStorageFullOverlay = false;
  }

  dismiss(triggerKey: string): void {
    this.dismissedThisSession.add(triggerKey);
    this.visibleBanners = this.visibleBanners.filter(
      (v) => v.banner.trigger_key !== triggerKey
    );
  }

  /** ngFor identity, so a re-render never swaps out a live DOM node. */
  trackByTriggerKey(_index: number, view: BannerView): string {
    return view.banner.trigger_key;
  }

  trackByLabel(_index: number, cta: BannerCTA): string {
    return cta.label;
  }

  private buildViews(banners: StatusBanner[]): BannerView[] {
    return this.filterDismissed(banners).map((banner) => ({
      banner,
      message: this.buildMessage(banner),
      ctas: this.buildCTAs(banner),
    }));
  }

  private buildCTAs(banner: StatusBanner): BannerCTA[] {
    if (!banner.cta_text) return [];
    const isRenew = banner.cta_text.toLowerCase().includes('renew');
    if (isRenew) {
      return [{ label: 'More Info', route: '/choose-subscription', queryParams: {} }];
    }
    return [{
      label: banner.cta_text,
      route: '/subscription-action',
      queryParams: { action: 'upgrade' },
    }];
  }

  getBannerClass(color: string): string {
    const map: Record<string, string> = {
      info: 'as-banner--info',
      warning: 'as-banner--warning',
      danger: 'as-banner--danger',
      red: 'as-banner--danger',
      orange: 'as-banner--warning',
    };
    return map[color] ?? 'as-banner--info';
  }

  private static readonly STORAGE_TRIGGER_KEYS = new Set(['warn_75', 'warn_90', 'warn_95', 'full']);

  private buildMessage(banner: StatusBanner): string {
    let message = banner.message;

    if (banner.trigger_key === 'grace' && message.includes('X')) {
      const graceEndsAt = this.status?.plan?.grace_ends_at;
      if (graceEndsAt) {
        const msLeft = new Date(graceEndsAt).getTime() - Date.now();
        const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
        message = message.replace('X', String(daysLeft));
      }
    }

    if (AccountStatusBannerComponent.STORAGE_TRIGGER_KEYS.has(banner.trigger_key)) {
      const storage = this.status?.storage;
      if (storage != null) {
        message += ` (${storage.used_gb.toFixed(1)} GB / ${storage.total_gb.toFixed(1)} GB)`;
      }
    }

    return message;
  }

  get expiredDateLabel(): string | null {
    const plan = this.status?.plan;
    if (!plan) return null;
    return plan.grace_ends_at || plan.expiry_date || null;
  }

  private filterDismissed(banners: StatusBanner[]): StatusBanner[] {
    return banners.filter(
      (b) => !b.is_dismissible || !this.dismissedThisSession.has(b.trigger_key)
    );
  }
}
